import { ChangeDetectionStrategy, Component, computed, inject, output, signal } from '@angular/core';
import { CreateMonitoring } from '../../services/create-monitoring';
import {
  daysBetween,
  earliestMonitoringStart,
  formatMonitoringDay,
  fromIsoDate,
  isValidMonitoringEnd,
  isValidMonitoringStart,
  latestMonitoringEnd,
  matchingDurationPreset,
  matchingStartPreset,
  MonitoringDurationPreset,
  MonitoringStartPreset,
  monitoringEnd,
  monitoringStart,
  monitoringTimeline,
  startOfLocalDay,
  toIsoDate,
} from './monitoring-window.helpers';

export type MonitoringRange = { start: Date; end: Date };

/**
 * Monitoring window: start up to 30 days back, then watch for up to a year.
 */
@Component({
  selector: 'cmb-monitoring-window',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './monitoring-window.html',
  styleUrl: './monitoring-window.scss',
})
export class MonitoringWindow {
  readonly createMonitoringService = inject(CreateMonitoring);
  readonly confirmed = output<MonitoringRange>();

  readonly startPresets: { id: MonitoringStartPreset; label: string }[] = [
    { id: '30d', label: '30 days ago' },
    { id: '14d', label: '14 days ago' },
    { id: 'today', label: 'Today' },
  ];

  readonly durationPresets: { id: MonitoringDurationPreset; label: string }[] = [
    { id: '1m', label: '1 month' },
    { id: '3m', label: '3 months' },
    { id: '6m', label: '6 months' },
    { id: '1y', label: '1 year' },
  ];

  private readonly durationChoice = signal<MonitoringDurationPreset | 'custom' | null>(null);

  readonly selectedStart = computed<MonitoringStartPreset | null>(() => {
    const start = this.createMonitoringService.monitorStart();
    return start ? matchingStartPreset(start, new Date()) : null;
  });

  readonly selectedDuration = computed<MonitoringDurationPreset | 'custom' | null>(() => {
    const chosen = this.durationChoice();
    if (chosen) {
      return chosen;
    }
    const start = this.createMonitoringService.monitorStart();
    const end = this.createMonitoringService.monitorUntil();
    if (!start || !end) {
      return null;
    }
    return matchingDurationPreset(start, end);
  });

  readonly fromLabel = computed(() => {
    const start = this.createMonitoringService.monitorStart();
    return start ? formatMonitoringDay(start) : 'Choose a start';
  });

  readonly toLabel = computed(() => {
    const end = this.createMonitoringService.monitorUntil();
    return end ? formatMonitoringDay(end) : 'Choose an end';
  });

  readonly dayCount = computed(() => {
    const start = this.createMonitoringService.monitorStart();
    const end = this.createMonitoringService.monitorUntil();
    if (!start || !end) {
      return null;
    }
    return daysBetween(start, end);
  });

  readonly timeline = computed(() => monitoringTimeline(
    this.createMonitoringService.monitorStart() ?? null,
    this.createMonitoringService.monitorUntil() ?? null,
    new Date(),
  ));

  readonly trackStartLabel = computed(() => formatMonitoringDay(earliestMonitoringStart(new Date())));

  readonly trackEndLabel = computed(() => formatMonitoringDay(latestMonitoringEnd(startOfLocalDay(new Date()))));

  readonly hint = computed(() => {
    const start = this.createMonitoringService.monitorStart() ?? startOfLocalDay(new Date());
    return `Start up to 30 days back (shaded). End up to 1 year after the start: latest ${formatMonitoringDay(latestMonitoringEnd(start))}.`;
  });

  readonly startInput = computed(() => {
    const start = this.createMonitoringService.monitorStart();
    return start ? toIsoDate(start) : '';
  });

  readonly endInput = computed(() => {
    const end = this.createMonitoringService.monitorUntil();
    return end ? toIsoDate(end) : '';
  });

  readonly earliestInput = computed(() => toIsoDate(earliestMonitoringStart(new Date())));

  readonly todayInput = computed(() => toIsoDate(startOfLocalDay(new Date())));

  readonly endMinInput = computed(() => {
    const start = this.createMonitoringService.monitorStart();
    const next = start ? startOfLocalDay(start) : startOfLocalDay(new Date());
    next.setDate(next.getDate() + 1);
    return toIsoDate(next);
  });

  readonly endMaxInput = computed(() => {
    const start = this.createMonitoringService.monitorStart() ?? startOfLocalDay(new Date());
    return toIsoDate(latestMonitoringEnd(start));
  });

  readonly rangeReady = computed(() => {
    const start = this.createMonitoringService.monitorStart();
    const end = this.createMonitoringService.monitorUntil();
    if (!start || !end) {
      return false;
    }
    return isValidMonitoringStart(start, new Date()) && isValidMonitoringEnd(start, end);
  });

  chipClass(selected: boolean): string {
    return selected
      ? 'btn-shout btn-shout-xs btn-shout-primary shrink-0'
      : 'btn-shout btn-shout-xs btn-shout-secondary shrink-0';
  }

  selectStart(preset: MonitoringStartPreset): void {
    const today = new Date();
    const start = monitoringStart(preset, today);
    const duration = this.activeDuration();
    this.createMonitoringService.monitorStart.set(start);
    if (duration === 'custom') {
      const end = this.createMonitoringService.monitorUntil();
      if (!end || !isValidMonitoringEnd(start, end)) {
        this.createMonitoringService.monitorUntil.set(undefined);
      }
      return;
    }
    if (duration) {
      this.createMonitoringService.monitorUntil.set(monitoringEnd(start, duration));
    }
  }

  selectDuration(preset: MonitoringDurationPreset): void {
    const today = new Date();
    const start = this.createMonitoringService.monitorStart() ?? monitoringStart('today', today);
    this.durationChoice.set(preset);
    if (!this.createMonitoringService.monitorStart()) {
      this.createMonitoringService.monitorStart.set(start);
    }
    this.createMonitoringService.monitorUntil.set(monitoringEnd(start, preset));
  }

  chooseCustom(): void {
    this.durationChoice.set('custom');
  }

  onCustomStart(event: Event): void {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) {
      return;
    }
    const date = fromIsoDate(target.value);
    const today = new Date();
    if (!date || !isValidMonitoringStart(date, today)) {
      return;
    }
    this.durationChoice.set('custom');
    this.createMonitoringService.monitorStart.set(date);
    const end = this.createMonitoringService.monitorUntil();
    if (end && !isValidMonitoringEnd(date, end)) {
      this.createMonitoringService.monitorUntil.set(undefined);
    }
  }

  onCustomEnd(event: Event): void {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) {
      return;
    }
    const start = this.createMonitoringService.monitorStart();
    const date = fromIsoDate(target.value);
    if (!start || !date || !isValidMonitoringEnd(start, date)) {
      return;
    }
    this.durationChoice.set('custom');
    this.createMonitoringService.monitorUntil.set(date);
  }

  useRange(): void {
    const start = this.createMonitoringService.monitorStart();
    const end = this.createMonitoringService.monitorUntil();
    if (!start || !end || !this.rangeReady()) {
      return;
    }
    this.confirmed.emit({
      start: new Date(start.getTime()),
      end: new Date(end.getTime()),
    });
  }

  private activeDuration(): MonitoringDurationPreset | 'custom' | null {
    const chosen = this.durationChoice();
    if (chosen) {
      return chosen;
    }
    const start = this.createMonitoringService.monitorStart();
    const end = this.createMonitoringService.monitorUntil();
    if (!start || !end) {
      return null;
    }
    return matchingDurationPreset(start, end);
  }
}
